<template>
	<div id="invoice-details" class="invoice-lightbox">
		<header class="invoice-toolbar" v-if="invoice">
			<div class="invoice-identity">
				<span class="invoice-mark" aria-hidden="true">
					<portal-icon name="receipt-text" :size="19"></portal-icon>
				</span>
				<h1>{{ $t('invoice.invoice-number-appended', { invoiceNumber: invoice.invoiceNumberFormatted }) }}</h1>
			</div>

			<div class="invoice-actions">
				<v-menu
					v-if="$vuetify.breakpoint.mdAndUp"
					:close-on-click="true"
					:nudge-bottom="8"
					offset-y
					:disabled="!paymentCount"
				>
					<template v-slot:activator="{ on, attrs }">
						<button
							type="button"
							class="toolbar-pill history-action"
							:disabled="!paymentCount"
							v-bind="attrs"
							v-on="on"
						>
							<portal-icon name="history" :size="16"></portal-icon>
							<span>{{ $t('invoices.details.payment-history') }}</span>
							<span class="action-count" v-if="paymentCount">{{ paymentCount }}</span>
						</button>
					</template>
					<payment-history :invoice="invoice" @openPayment="openPaymentDetail($event)"></payment-history>
				</v-menu>

				<button
					v-if="canPay"
					type="button"
					class="toolbar-pill pay-action"
					:aria-label="$t('invoices.pay-now')"
					@click="goToPaymentPage"
				>
					<portal-icon name="credit-card" :size="16"></portal-icon>
					<span class="action-label">{{ $t('invoices.pay-now') }}</span>
				</button>

				<button
					type="button"
					class="round-action"
					:aria-label="$t('proposals.print-save-pdf')"
					:title="$t('proposals.print-save-pdf')"
					@click="downloadInvoice"
				>
					<portal-icon name="download" :size="19"></portal-icon>
				</button>
				<button
					type="button"
					class="round-action close-action"
					:aria-label="$t('global.close')"
					:title="$t('global.close')"
					@click="handleClose"
				>
					<portal-icon name="x" :size="19"></portal-icon>
				</button>
			</div>
		</header>

		<div class="invoice-loading" v-if="!invoice || !account">
			<v-progress-circular indeterminate :size="38" :width="3" color="primary"></v-progress-circular>
		</div>

		<div class="invoice-document show-scrollbar" v-else>
			<component
				class="invoice-renderer-host"
				:is="rendererType"
				:invoice="invoice"
				:scheduled-payments="invoice.scheduledPayments"
				:account="account"
				:edit-mode="false"
				:key="refreshKey"
				:attachment-service="invoiceService"
				:invoice-service="invoiceService"
				:appearance="effectiveAppearance"
				@pay-now="goToPaymentPage"
			></component>
		</div>
	</div>
</template>

<script>
	import InvoiceService from '@/modules/invoices/InvoiceService';
  import InvoiceRenderer from '@/modules/invoices/InvoiceRenderer/InvoiceRenderer';
  import PaymentHistory from '@/modules/invoices/InvoiceRenderer/PaymentHistory';
  import PaymentDetailsModal from '@/modules/invoices/InvoiceRenderer/PaymentDetailsModal';
  import InvoiceRendererV2 from '@/modules/invoices/invoiceRendererV2/InvoiceRendererV2';
	import { documentBrandAppearance, resolveDocumentBranding } from '@/utils/portalBranding';

  export default {
		name: 'InvoiceDetails',

		props: ['invoiceId'],

		components: {InvoiceRenderer,InvoiceRendererV2,PaymentHistory},

		data: function() {
			return {
        refreshKey: 0,
        invoice: null,
        account: null,
				capturedBranding: null,
        invoiceService: new InvoiceService(),
      };
		},

		mounted() {
      this.invoiceService.getInvoice(this.invoiceId).then((res) => {
        this.invoice = res.data.invoice;
				this.capturedBranding = this.invoice.invoiceTemplate && this.invoice.invoiceTemplate.branding;
				this.applyDocumentBranding();
        this.account = res.data.account;
        this.invoiceService.setInvoice(this.invoice);
      });
    },

		methods: {
      handleClose: function(){
        this.$emit('result');
      },

      openPaymentDetail(payment){
        let binding = {
          invoice: this.invoice,
          payment: payment,
          editMode: false
        };
        this.$store.state.globalModalController.openModal(PaymentDetailsModal, binding, true, false);
      },

      downloadInvoice: function(){
        this.$store.commit('startLoading');
        this.invoiceService
            .downloadInvoice(this.effectiveAppearance)
            .then((res) => {
              let fileURL = window.URL.createObjectURL(new Blob([res.data]));
              let fileLink = document.createElement('a');
              fileLink.href = fileURL;
              fileLink.setAttribute('download', 'Invoice-' + this.invoice.invoiceNumberFormatted + '.pdf');
              document.body.appendChild(fileLink);
              fileLink.click();
            }).finally(() => this.$store.commit('stopLoading'));
      },

      goToPaymentPage: function() {
        if (!this.invoice.payOnlineUrl) return;
        const token = new URL(this.invoice.payOnlineUrl).searchParams.get('token');
        window.open(window.location.origin + '/payment?token=' + token, '_blank');
      },

			applyDocumentBranding() {
				if (!this.capturedBranding || !this.invoice || !this.invoice.invoiceTemplate) return;
				this.$set(
					this.invoice.invoiceTemplate,
					'branding',
					resolveDocumentBranding(this.capturedBranding, this.effectiveAppearance)
				);
			},
    },

		computed: {
			effectiveAppearance() {
				return documentBrandAppearance(
					this.capturedBranding,
					this.$vuetify.theme.dark ? 'dark' : 'light'
				);
			},
      rendererType: function(){
        return (this.invoice && this.invoice.invoiceTemplate) ? 'InvoiceRendererV2' : 'InvoiceRenderer';
      },
      canPay: function() {
        if (!this.invoice) return false;
        if (!this.invoice.payOnlineUrl) return false;
        if (!this.invoice.allowOnlinePayment) return false;
        return this.invoice.amountDue > 0 && this.invoice.statusDetail && this.invoice.statusDetail.payable;
      },

			paymentCount: function() {
				return this.invoice && this.invoice.payments ? this.invoice.payments.length : 0;
			},
    },

		watch: {
			'$vuetify.theme.dark'() {
				this.applyDocumentBranding();
			},
		},
	};
</script>

<style scoped lang="scss">
	.invoice-lightbox {
		height: 100%;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		background: var(--portal-canvas);
		color: var(--portal-text);
	}

	.invoice-toolbar {
		min-height: 66px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 10px 14px 10px 16px;
		border-bottom: 1px solid var(--portal-border);
		background: color-mix(in srgb, var(--portal-surface) 96%, transparent);
	}

	.invoice-identity {
		min-width: 0;
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.invoice-mark {
		width: 38px;
		height: 38px;
		flex: 0 0 38px;
		display: grid;
		place-items: center;
		border-radius: 50%;
		background: color-mix(in srgb, var(--v-primary-base) 10%, var(--portal-surface));
		color: var(--v-primary-base);
	}

	.invoice-identity h1 {
		margin: 0;
		overflow: hidden;
		color: var(--portal-text);
		font-size: 16px;
		font-weight: 650;
		line-height: 1.25;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.invoice-actions {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.toolbar-pill,
	.round-action {
		height: 40px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border: 1px solid var(--portal-border);
		background: var(--portal-surface);
		color: var(--portal-text);
		font: inherit;
		cursor: pointer;
		transition: border-color 140ms ease, background-color 140ms ease, color 140ms ease;
	}

	.toolbar-pill {
		gap: 7px;
		padding: 0 13px;
		border-radius: var(--brand-button-radius, 999px);
		font-size: 12px;
		font-weight: 650;
	}

	.round-action {
		width: 40px;
		flex: 0 0 40px;
		padding: 0;
		border-radius: var(--brand-button-radius, 50%);
		color: var(--portal-muted);
	}

	.toolbar-pill:hover:not(:disabled),
	.toolbar-pill:focus-visible,
	.round-action:hover,
	.round-action:focus-visible {
		outline: 0;
		border-color: color-mix(in srgb, var(--v-primary-base) 36%, var(--portal-border));
		background: color-mix(in srgb, var(--v-primary-base) 7%, var(--portal-surface));
		color: var(--v-primary-base);
	}

	.toolbar-pill:disabled {
		cursor: default;
		opacity: 0.46;
	}

	.pay-action {
		border-color: var(--v-primary-base);
		background: var(--v-primary-base);
		color: var(--v-white-base);
	}

	.pay-action:hover,
	.pay-action:focus-visible {
		border-color: var(--v-primary-base);
		background: color-mix(in srgb, var(--v-primary-base) 90%, black);
		color: var(--v-white-base);
	}

	.action-count {
		min-width: 20px;
		height: 20px;
		display: inline-grid;
		place-items: center;
		padding: 0 6px;
		border-radius: 999px;
		background: var(--v-gray_20-base);
		font-size: 10px;
	}

	.close-action {
		border-color: transparent;
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	.invoice-loading {
		min-height: 0;
		flex: 1;
		display: grid;
		place-items: center;
	}

	.invoice-document {
		min-height: 0;
		flex: 1;
		overflow: auto;
		background: var(--v-gray_20-base);
	}

	.invoice-renderer-host {
		height: 100%;
		max-height: 100%;
	}

	@media (max-width: 599px) {
		.history-action {
			display: none;
		}

		.invoice-toolbar {
			min-height: 58px;
			gap: 8px;
			padding: 8px 10px 8px 12px;
		}

		.invoice-mark {
			width: 34px;
			height: 34px;
			flex-basis: 34px;
		}

		.invoice-identity {
			gap: 8px;
		}

		.invoice-identity h1 {
			font-size: 15px;
		}

		.invoice-actions {
			gap: 5px;
		}

		.toolbar-pill,
		.round-action {
			width: 38px;
			height: 38px;
			flex: 0 0 38px;
			padding: 0;
			border-radius: var(--brand-button-radius, 50%);
		}

		.action-label {
			position: absolute;
			width: 1px;
			height: 1px;
			overflow: hidden;
			clip: rect(0 0 0 0);
			white-space: nowrap;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.toolbar-pill,
		.round-action {
			transition: none;
		}
	}
</style>
